/**
 * Desktop Dictation API
 *
 * TypeScript bindings for Fn/Globe key dictation. Recording happens in Rust
 * to avoid latency when WebView is suspended (minimized window).
 *
 * Flow: setDictationConfig → hold Fn → Rust records → release → transcribe → insert
 *
 * ## Naming Conventions
 *
 * - **Commands** use `dictation_` prefix (e.g., `dictation_set_config`) to avoid
 *   collisions with other app commands.
 * - **Events** use `dictation:` namespace (e.g., `dictation:start`).
 */

import { type UserContext } from '@/features/dictation/dictationBubble/store/buildUserContext';
import { type DictationTriggeredBy } from '@/models/analytics/features/dictation';
import { isTauri } from '@/tauri/utils';
import { type DictationTone } from '@heidi/dictate';
import {
  type DeviceButtonMappings,
  type DictaphoneTriggerMode,
  sanitizeDeviceButtonMappings,
} from '@heidi/dictate/data';
import { invoke } from '@tauri-apps/api/core';
import { emit, type EventCallback, listen } from '@tauri-apps/api/event';

const COMMAND_PREFIX = 'dictation_';

/** Invoke a dictation command with the `dictation_` prefix. */
async function invokeDictationCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  return invoke<T>(`${COMMAND_PREFIX}${command}`, payload ?? {});
}

/**
 * Result of a native dictation operation. Matches Rust's DictationResult.
 *
 * `targetApp` = app dictated into (bundle id / exe name) or `"heidi"`; absent
 * when unknown.
 */
export type DictationResult =
  | {
      type: 'success';
      text: string;
      durationMs: number;
      targetApp?: string;
    }
  | {
      type: 'insertionFailed';
      text: string;
      durationMs: number;
      reason: string;
      targetApp?: string;
    }
  | { type: 'noAudio' }
  | { type: 'emptyTranscription'; durationMs: number }
  | { type: 'error'; reason: string; errorCode?: string };

/**
 * Payload for `dictation:start` event. Also used for `dictation:handsfree-start`.
 *
 * SCR-4118: `dictationId` / `dictationRecordingId` are populated once Rust
 * has actually started recording (manager has minted the IDs). Rejection
 * paths (pipeline busy, permission denied) omit them. The web side writes
 * them onto the dictation store so subsequent client log lines correlate
 * with the backend `dictation_log` row.
 *
 * SCR-4525: `triggeredBy` discriminates the originating trigger so the
 * widget's analytics dispatch and the main-window state subscription can
 * fork on it (button vs. hotkey).
 */
export interface DictationStartPayload {
  recordingStarted: boolean;
  triggeredBy: DictationTriggeredBy;
  error?: string;
  dictationId?: string;
  dictationRecordingId?: string;
  /**
   * True only when a requested named Dictate mic was unavailable and the OS
   * default was used successfully. Explicit OS-default preference is false.
   * SCR-6773.
   */
  usedDefaultInputFallback?: boolean;
  /**
   * True only when a requested named Dictate mic was unavailable and the
   * session mic was used instead.
   */
  usedSessionInputFallback?: boolean;
}

/**
 * Payload for `dictation:processing` event.
 *
 * SCR-4118: previously emitted as `void`. Now carries the per-session
 * correlation IDs so the widget can tag PROCESSING-state logs with the
 * same pair the chunk POST and the eventual result event use.
 */
export interface DictationProcessingPayload {
  dictationId?: string;
  dictationRecordingId?: string;
}

/** Payload for `dictation:result` event. */
export interface DictationResultPayload {
  result: DictationResult;
  dictationId?: string;
  dictationRecordingId?: string;
}

/**
 * Set dictation feature enabled state. Syncs FF value to Rust.
 * On first enable, initializes hotkey listener and audio.
 * FF value is checked when processing hotkey events (auth0 pattern).
 */
export async function setDictationEnabled(enabled: boolean): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_enabled', { enabled });
}

/**
 * Sync the remote-desktop keystroke kill-switch (SCR-4586) to Rust. When off,
 * dictation into remote-desktop clients (Citrix, RDP, ...) falls back to the
 * AX/clipboard path instead of typing the transcript as synthetic keystrokes.
 */
export async function setDictationRemoteDesktopKeystrokeEnabled(
  enabled: boolean
): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_remote_desktop_keystroke_enabled', { enabled });
}

/**
 * Sync the SCR-7481 browser-field detection rollout gate to Rust. When off,
 * Windows text-field detection is the pre-SCR-7481 ladder (exact Chromium
 * classes, active `TextPattern2` caret only, tao-only main-window focus).
 */
export async function setDictationBrowserFieldDetectionEnabled(
  enabled: boolean
): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_browser_field_detection_enabled', { enabled });
}

/** Sync the RecMic / PowerMic / SpeechMike HID reader kill switch to Rust. */
export async function setDictationHardwareMicEnabled(enabled: boolean): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_hardware_mic_enabled', { enabled });
}

/** Mirrors Rust `HardwareMicModel` (serde snake_case). */
export type HardwareMicModel = 'rec_mic' | 'power_mic_3' | 'power_mic_4' | 'speech_mike';

/** Mirrors Rust `HardwareMicDevice`. */
export interface HardwareMicDevice {
  model: HardwareMicModel;
  product: string | null;
  serial: string | null;
  vid: number;
  pid: number;
}

/**
 * Mirrors Rust `HardwareMicConnected`. `hotPlug` is false for devices a reader
 * session finds already connected when it starts (boot, flag on, resume).
 */
export interface HardwareMicConnectedPayload extends HardwareMicDevice {
  hotPlug: boolean;
}

export const HARDWARE_MIC_CONNECTED_EVENT = 'dictation:hardware-mic-connected';
export const HARDWARE_MIC_DISCONNECTED_EVENT = 'dictation:hardware-mic-disconnected';
export const HARDWARE_MIC_BUTTON_PRESSED_EVENT = 'dictation:hardware-mic-button-pressed';

/**
 * Payload for `dictation:hardware-mic-button-pressed`. `action` is omitted for unmapped
 * presses and for start/stop: Rust already handles start/stop natively, so omitting it
 * means no listener can dispatch it a second time.
 */
export interface HardwareMicButtonPressedPayload {
  deviceKey: string;
  buttonId: string;
  action?: {
    type: string;
    snippetId?: string;
  };
}

/** Listen for an identified hardware-mic button press. */
export async function listenHardwareMicButtonPressed(
  cb: EventCallback<HardwareMicButtonPressedPayload>
) {
  if (!isTauri()) return () => {};
  return listen<HardwareMicButtonPressedPayload>(HARDWARE_MIC_BUTTON_PRESSED_EVENT, cb);
}

/** List currently-connected hardware dictation mics from the HID reader's snapshot. */
export async function listHardwareMics(): Promise<HardwareMicDevice[]> {
  if (!isTauri()) return [];
  return invokeDictationCommand<HardwareMicDevice[]>('list_hardware_mics');
}

/** Listen for a hardware mic connecting (plug-in or the reader's first snapshot). */
export async function listenHardwareMicConnected(
  cb: EventCallback<HardwareMicConnectedPayload>
) {
  if (!isTauri()) return () => {};
  return listen<HardwareMicConnectedPayload>(HARDWARE_MIC_CONNECTED_EVENT, cb);
}

/** Listen for a hardware mic disconnecting (unplug or flag off). */
export async function listenHardwareMicDisconnected(
  cb: EventCallback<HardwareMicDevice>
) {
  if (!isTauri()) return () => {};
  return listen<HardwareMicDevice>(HARDWARE_MIC_DISCONNECTED_EVENT, cb);
}

/**
 * Sync per-device button mappings to Rust. The command itself lands in
 * SCR-7495; this wrapper is the TS bridge only.
 */
export async function setDeviceButtonMappings(
  mappings: DeviceButtonMappings
): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_device_button_mappings', {
    mappings: sanitizeDeviceButtonMappings(mappings),
  });
}

/**
 * While the map-functionality dialog is open, device presses emit identity
 * events but never hold dictation (mapped or the v1 record default).
 */
export async function setButtonCaptureMode(enabled: boolean): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_button_capture_mode', { enabled });
}

/**
 * How a quick press of the dictaphone start/stop button enters hands-free on
 * every connected device: double press or single press. Holding the button
 * records until release either way.
 */
export async function setHardwareTriggerMode(mode: DictaphoneTriggerMode): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_hardware_trigger_mode', { mode });
}

/** Synthesize an OS-level Tab into the frontmost app. */
export async function pressOsTab(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('press_tab');
}

/** Synthesize an OS-level Shift-Tab into the frontmost app. */
export async function pressOsShiftTab(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('press_shift_tab');
}

/** Listen for dictation start (recording began). Payload indicates if recording started. */
export async function listenDictationStart(cb: EventCallback<DictationStartPayload>) {
  if (!isTauri()) return () => {};
  return listen<DictationStartPayload>('dictation:start', cb);
}

/** Listen for dictation entering the processing phase (recording stopped, transcribing). */
export async function listenDictationProcessing(
  cb: EventCallback<DictationProcessingPayload>
) {
  if (!isTauri()) return () => {};
  return listen<DictationProcessingPayload>('dictation:processing', cb);
}

/**
 * Insert snippet text into the external app that is frontmost, routed like
 * dictation: Accessibility / native edit, else clipboard paste, and keystrokes
 * only for remote-desktop targets. Does nothing while Heidi is frontmost.
 */
export async function typeSnippetText(text: string): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('type_text', { text });
}

/** Listen for dictation result. Payload contains the complete DictationResult. */
export async function listenDictationResult(cb: EventCallback<DictationResultPayload>) {
  if (!isTauri()) return () => {};
  return listen<DictationResultPayload>('dictation:result', cb);
}

/**
 * Which transcription auth surface native dictation targets. Mirrors Rust's
 * `DictationAuthMode` (serde `snake_case`). `user_jwt` is the default
 * authenticated path; the anonymous modes back the dictate onboarding flow.
 */
export type DictationAuthMode = 'user_jwt' | 'anonymous_onboarding' | 'anonymous_trial';

/** Optional tail for {@link setDictationConfig}. */
export interface SetDictationConfigOptions {
  country?: string;
  userContext?: UserContext;
  /** App-identifier → tone map for per-app styling. Omit to disable. */
  appToneMap?: Record<string, DictationTone>;
  /** Tone for undetected/unmapped apps. Omit → backend `formal`. */
  fallbackTone?: DictationTone;
  /** Omit when the opt-out FF is off — backend defaults to logging. */
  logDictationsEnabled?: boolean;
  /** Omit when the verbal-commands settings FF is off — backend defaults to enabled. */
  verbalCommandsEnabled?: boolean;
  /**
   * Whether native dictation cues (start/stop/error) play. Sourced from
   * `user.dictation.sound_enabled`. Omit → Rust defaults to enabled (sounds on).
   */
  soundEnabled?: boolean;
  /** Effective value (flag AND preference). Omit → Rust defaults to no read. */
  cursorAwarenessEnabled?: boolean;
  /** Omit (or `user_jwt`) for authenticated dictation; onboarding passes an anonymous mode. */
  authMode?: DictationAuthMode;
  /**
   * Locks dictation force-refresh. Omit → legacy FE re-config handshake.
   */
  rustAuth0TokenRefresh?: boolean;
}

/**
 * Configure native dictation. Call on login or when transcription settings change.
 * Includes user context for inline post-processing (eliminates separate ml-scribe call).
 */
export async function setDictationConfig(
  transcriptionBaseUrl: string,
  authToken: string,
  languages: string[],
  options?: SetDictationConfigOptions
): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_config', {
    params: {
      transcriptionBaseUrl,
      authToken,
      languages,
      country: options?.country,
      userContext: options?.userContext,
      appToneMap: options?.appToneMap,
      fallbackTone: options?.fallbackTone,
      logDictationsEnabled: options?.logDictationsEnabled,
      verbalCommandsEnabled: options?.verbalCommandsEnabled,
      soundEnabled: options?.soundEnabled,
      cursorAwarenessEnabled: options?.cursorAwarenessEnabled,
      authMode: options?.authMode,
      rustAuth0TokenRefresh: options?.rustAuth0TokenRefresh,
    },
  });
}

/**
 * Enable/disable dictation for the dictate onboarding flow, independently of the
 * user feature flag. Lets onboarding teach the global hotkey pre-auth (anonymous
 * token) and immediately post-sign-in (before `user.dictation.enabled` is set).
 */
export async function setOnboardingDictationEnabled(enabled: boolean): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_onboarding_enabled', { enabled });
}

/** Check if native dictation is configured. */
export async function isDictationConfigured(): Promise<boolean> {
  if (!isTauri()) return false;
  return invokeDictationCommand<boolean>('is_configured');
}

/**
 * Pin dictation to a specific input device. Pass `null` to track the OS default.
 * Device names come from `AudioDevice.name` returned by the audio-recorder plugin
 * (the CPAL device display name — same convention session recording uses). If the
 * device is unavailable at hotkey time, Rust falls back to the session mic (see
 * `setDictationInputDeviceFallback`), then the system default, so dictation
 * never fails to start.
 */
export async function setDictationInputDevice(deviceName: string | null): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_input_device', { deviceName });
}

/**
 * Tell Rust the session recording mic, used when the pinned Dictate device is
 * unavailable at hotkey time. Pass `null` when the session mic tracks the OS
 * default.
 */
export async function setDictationInputDeviceFallback(
  deviceName: string | null
): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_input_device_fallback', { deviceName });
}

/** Cancel native dictation recording in progress. */
export async function cancelNativeDictation(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('cancel');
}

/** Stop recording and process (transcribe + insert). Used for handsfree mode. */
export async function stopNativeDictation(): Promise<DictationResult> {
  if (!isTauri()) return { type: 'error', reason: 'Not in Tauri environment' };
  return invokeDictationCommand<DictationResult>('stop');
}

/**
 * Start native dictation programmatically — used by the in-app `DictateButton`
 * on desktop. Mirrors the handsfree-hotkey path on the Rust side; emits
 * `dictation:handsfree-start` with `triggeredBy: 'button_click_evidence'`.
 *
 * SCR-4525. Rejects (rejected promise) if a dictation is already in flight.
 */
export async function startProgrammaticDictation(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('start_programmatic');
}

/** Listen for handsfree recording start (on double-tap). */
export async function listenHandsfreeStart(cb: EventCallback<DictationStartPayload>) {
  if (!isTauri()) return () => {};
  return listen<DictationStartPayload>('dictation:handsfree-start', cb);
}

/**
 * Listen for the anonymous dictation quota being exhausted. Rust emits this
 * (and shows the widget) when the hotkey is pressed after the per-identity
 * limit has been reached, instead of capturing audio the backend would reject.
 * The widget responds by showing the sign-in prompt.
 */
export async function listenDictationAnonymousLimitReached(cb: EventCallback<void>) {
  if (!isTauri()) return () => {};
  return listen<void>('dictation:anonymous-limit-reached', cb);
}

/** Payload for `dictation:result-suppressed` event. */
export interface DictationResultSuppressedPayload {
  suppressed: boolean;
}

/**
 * Emit when the dictation onboarding modal opens/closes. The widget uses
 * this to suppress its RESULT bubble — dictation still runs, only the
 * copy-to-clipboard floating bubble is skipped. INSERTION_FALLBACK and
 * ERROR states are unaffected (user must still recover/see those).
 */
export async function emitDictationResultSuppressed(suppressed: boolean): Promise<void> {
  if (!isTauri()) return;
  await emit('dictation:result-suppressed', { suppressed });
}

/** Listen for the suppression signal on the widget side. */
export async function listenDictationResultSuppression(
  cb: EventCallback<DictationResultSuppressedPayload>
) {
  if (!isTauri()) return () => {};
  return listen<DictationResultSuppressedPayload>('dictation:result-suppressed', cb);
}

export type DictationSoundType = 'start' | 'stop' | 'error';

/** Play dictation sound via native audio. Works when WebView is suspended. */
export async function playDictationSound(soundType: DictationSoundType): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('play_sound', { soundType });
}

/** Initialize native audio output. Auto-called when FF enables dictation, rarely needed directly. */
export async function initDictationAudio(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('init_audio');
}

/**
 * Show restart dialog after granting accessibility permission.
 * Returns true if user chose to restart, false if "Later".
 */
export async function showAccessibilityRestartDialog(): Promise<boolean> {
  if (!isTauri()) return false;
  return invokeDictationCommand<boolean>('show_restart_dialog');
}

/** Start key-recording mode: next OS-level key press emits `hotkey:key_captured`. */
export async function startKeyRecording(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('start_key_recording');
}

/** Stop key-recording mode. */
export async function stopKeyRecording(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('stop_key_recording');
}

/**
 * Set the configured dictation hotkey. Accepts the canonical chord shape
 * (modifiers first, optional terminal key) and forwards it to the Rust IPC
 * command `dictation_set_hotkey`, which resolves the names into a
 * `HotkeyCombo` via the canonical name vocabulary (see
 * `src-tauri/src/global_hotkey/key_names.rs`).
 *
 * `key === null` denotes a modifier-only chord (e.g. the macOS default `Fn`).
 */
export async function setDictationHotkey(
  modifiers: string[],
  key: string | null
): Promise<void> {
  if (!isTauri()) return;
  // No-op on empty chord: Rust's `resolve_combo` falls back to the platform
  // default anyway, and turning this into a throw makes it an unhandled
  // rejection from any `void`-discarded caller (e.g. the preview useEffect
  // on first render). Caller is expected to gate on `hasChord` upstream.
  if (modifiers.length === 0 && key === null) return;
  return invokeDictationCommand('set_hotkey', { modifiers, key });
}

/** Enable native chord preview for dictate onboarding (macOS Fn / Win Right Ctrl). */
export async function setChordPreviewEnabled(enabled: boolean): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('set_chord_preview_enabled', { enabled });
}

export interface ChordPreviewPayload {
  engaged: boolean;
}

/** Listen for native chord engagement while onboarding preview is active. */
export async function listenChordPreview(cb: EventCallback<ChordPreviewPayload>) {
  if (!isTauri()) return () => {};
  return listen<ChordPreviewPayload>('hotkey:chord_preview', cb);
}

/**
 * Payload for `hotkey:key_captured`. Emitted by the platform listener while
 * `IS_KEY_RECORDING` is true. Every payload now carries `keyName`,
 * `modifiers`, and `isModifier` alongside the raw `keyCode` (macOS) or
 * `vkCode` (Windows). Fields remain optional in the type so the FE can bail
 * gracefully if a future payload variant omits them.
 */
export interface KeyCapturePayload {
  /** macOS CGKeyCode (populated by the macOS listener). */
  keyCode?: number;
  /** Windows VK code (populated by the Windows listener). */
  vkCode?: number;
  /** Canonical key name. */
  keyName?: string;
  /** Modifier-key boolean map. */
  modifiers?: Record<string, boolean>;
  /** True if the captured key is a modifier. */
  isModifier?: boolean;
}

/** Listen for hotkey capture event. See `KeyCapturePayload` for shape. */
export async function listenKeyCapture(cb: EventCallback<KeyCapturePayload>) {
  if (!isTauri()) return () => {};
  return listen<KeyCapturePayload>('hotkey:key_captured', cb);
}

/** Hotkey chord persisted as part of pre-auth dictate onboarding. */
export interface DictateOnboardingHotkeyData {
  modifiers: string[];
  key: string | null;
}

/**
 * Pre-auth dictate onboarding state persisted to disk. `current_step`/`hotkey`
 * are transient resume fields (cleared on sign-in or completion);
 * `has_completed_dictation_onboarding` is the durable flag written once the flow ends.
 */
export interface DictateOnboardingData {
  current_step?: string | null;
  hotkey?: DictateOnboardingHotkeyData | null;
  has_completed_dictation_onboarding?: boolean | null;
}

/** Persist pre-auth dictate onboarding state to disk. */
export async function saveDictateOnboardingData(
  data: DictateOnboardingData
): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('save_onboarding_data', { data });
}

/** Load pre-auth dictate onboarding state from disk. */
export async function loadDictateOnboardingData(): Promise<DictateOnboardingData | null> {
  if (!isTauri()) return null;
  return invokeDictationCommand<DictateOnboardingData | null>('load_onboarding_data');
}

/** Remove onboarding data after the flow completes. */
export async function clearDictateOnboardingData(): Promise<void> {
  if (!isTauri()) return;
  return invokeDictationCommand('clear_onboarding_data');
}

/**
 * Listen for `hotkey:chord_committed` — fired when the user releases every
 * modifier they were holding without ever pressing a terminal key (i.e. a
 * modifier-only chord like `Fn` alone). Modifier set is carried by the
 * preceding `hotkey:key_captured` events; payload here is empty.
 */
export async function listenChordCommitted(cb: EventCallback<void>) {
  if (!isTauri()) return () => {};
  return listen<void>('hotkey:chord_committed', cb);
}
